<div class="database-mappings-container">
  <h1>Database Mappings</h1>
  <p class="subtitle">Manage source to target database relationships</p>

  <div class="error" *ngIf="error">
    {{ error }}
    <button class="close-btn" (click)="error = ''">✖</button>
  </div>

  <div class="actions-bar">
    <button class="btn btn-primary" (click)="openAddForm()">
      + Add New Mapping
    </button>
  </div>

  <div class="loading" *ngIf="loading">Loading mappings...</div>

  <div class="mappings-list" *ngIf="!loading && mappings.length > 0">
    <div class="mapping-card" *ngFor="let mapping of mappings" [class.inactive]="!mapping.isActive">
      <div class="mapping-header">
        <div class="mapping-info">
          <div class="database-pair">
            <span class="database-name source">{{ mapping.sourceDatabase }}</span>
            <span class="arrow">→</span>
            <span class="database-name target">{{ mapping.targetDatabase }}</span>
          </div>
          <div class="mapping-meta">
            <span class="description" *ngIf="mapping.description">{{ mapping.description }}</span>
            <span class="date">Created: {{ mapping.createdDate | date:'short' }}</span>
            <span class="badge" [class.active]="mapping.isActive" [class.inactive]="!mapping.isActive">
              {{ mapping.isActive ? 'Active' : 'Inactive' }}
            </span>
          </div>
        </div>
        <div class="mapping-actions">
          <button class="btn btn-sm btn-info" (click)="toggleVersionInfo(mapping)">
            {{ expandedMapping === mapping.mappingID ? 'Hide' : 'Show' }} Version Info
          </button>
          <button class="btn btn-sm btn-secondary" (click)="openEditForm(mapping)">Edit</button>
          <button class="btn btn-sm btn-danger" (click)="confirmDelete(mapping)">Delete</button>
        </div>
      </div>

      <!-- Version Info Section -->
      <div class="version-info-section" *ngIf="expandedMapping === mapping.mappingID">
        <div class="loading-spinner" *ngIf="isLoadingVersionInfo(mapping.mappingID)">
          Loading version information...
        </div>

        <ng-container *ngIf="!isLoadingVersionInfo(mapping.mappingID) && getVersionInfo(mapping.mappingID) as info">
          <div class="version-comparison">
            <div class="version-box source">
              <h4>Source: {{ info.sourceDatabase }}</h4>
              <div class="version-details" *ngIf="info.sourceVersionID">
                <div class="version-name">{{ info.sourceVersionName }}</div>
                <div class="version-date">{{ info.sourceVersionDate | date:'medium' }}</div>
              </div>
              <div class="no-version" *ngIf="!info.sourceVersionID">
                No versions found
              </div>
            </div>

            <div class="version-status">
              <div class="status-indicator" [class.synced]="!info.needsUpgrade" [class.behind]="info.needsUpgrade">
                <span *ngIf="!info.needsUpgrade">✓ Synced</span>
                <span *ngIf="info.needsUpgrade">{{ info.versionsBehind }} version(s) behind</span>
              </div>
            </div>

            <div class="version-box target">
              <h4>Target: {{ info.targetDatabase }}</h4>
              <div class="version-details" *ngIf="info.targetVersionID">
                <div class="version-name">{{ info.targetVersionName }}</div>
                <div class="version-date">{{ info.targetVersionDate | date:'medium' }}</div>
              </div>
              <div class="no-version" *ngIf="!info.targetVersionID">
                No versions found
              </div>
            </div>
          </div>

          <!-- Versions to upgrade list -->
          <div class="versions-to-upgrade" *ngIf="info.needsUpgrade && info.versionsToUpgrade.length > 0">
            <h5>Versions to Apply:</h5>
            <div class="version-list">
              <div class="version-item" *ngFor="let v of info.versionsToUpgrade">
                <span class="version-name">{{ v.versionName }}</span>
                <span class="version-date">{{ v.versionDate | date:'short' }}</span>
                <span class="log-count">{{ v.logCount }} log(s)</span>
              </div>
            </div>
          </div>

          <div class="version-actions">
            <button class="btn btn-sm btn-secondary" (click)="refreshVersionInfo(mapping.mappingID)">
              ↻ Refresh
            </button>
            <button class="btn btn-success"
                    *ngIf="info.needsUpgrade"
                    (click)="confirmUpgrade(mapping)">
              ⬆ Upgrade Target Database
            </button>
          </div>
        </ng-container>
      </div>
    </div>
  </div>

  <div class="no-data" *ngIf="!loading && mappings.length === 0">
    No database mappings configured. Click "Add New Mapping" to create one.
  </div>

  <!-- Add/Edit Modal -->
  <div class="modal" *ngIf="showAddForm" (click)="cancelForm()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <button class="close-btn" (click)="cancelForm()">✖</button>
      <h2>{{ editingMapping ? 'Edit Mapping' : 'Add New Mapping' }}</h2>
      
      <div class="form-group">
        <label>Source Database:</label>
        <select [(ngModel)]="newMapping.sourceDatabase" class="select-input">
          <option value="">-- Select Source --</option>
          <option *ngFor="let db of availableSourceDatabases" [value]="db">{{ db }}</option>
        </select>
        <small class="hint">Tracked databases with DDL log history</small>
      </div>

      <div class="form-group">
        <label>Target Database:</label>
        <select [(ngModel)]="newMapping.targetDatabase" class="select-input">
          <option value="">-- Select Target --</option>
          <option *ngFor="let db of availableTargetDatabases" [value]="db"
                  [disabled]="db === newMapping.sourceDatabase">{{ db }}</option>
        </select>
        <small class="hint">Untracked databases (e.g. production)</small>
      </div>

      <div class="form-group">
        <label>Description (optional):</label>
        <input type="text" [(ngModel)]="newMapping.description" class="input" 
               placeholder="e.g., Development to Beta sync" />
      </div>

      <div class="form-group checkbox-group">
        <label>
          <input type="checkbox" [(ngModel)]="newMapping.isActive" />
          Active
        </label>
      </div>

      <div class="modal-actions">
        <button class="btn btn-secondary" (click)="cancelForm()">Cancel</button>
        <button class="btn btn-primary" (click)="saveMapping()">
          {{ editingMapping ? 'Update' : 'Create' }} Mapping
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <div class="modal" *ngIf="showDeleteModal" (click)="cancelDelete()">
    <div class="modal-content small" (click)="$event.stopPropagation()">
      <button class="close-btn" (click)="cancelDelete()">✖</button>
      <h2>Confirm Delete</h2>
      <p>Are you sure you want to delete the mapping:</p>
      <p class="mapping-preview">
        <strong>{{ mappingToDelete?.sourceDatabase }}</strong> →
        <strong>{{ mappingToDelete?.targetDatabase }}</strong>
      </p>
      <div class="modal-actions">
        <button class="btn btn-secondary" (click)="cancelDelete()">Cancel</button>
        <button class="btn btn-danger" (click)="executeDelete()">Delete</button>
      </div>
    </div>
  </div>

  <!-- Upgrade Confirmation Modal -->
  <div class="modal" *ngIf="showUpgradeModal" (click)="!upgrading && cancelUpgrade()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <button class="close-btn" (click)="cancelUpgrade()" *ngIf="!upgrading">✖</button>
      <h2>Confirm Upgrade</h2>

      <ng-container *ngIf="!upgradeResult">
        <p>Are you sure you want to upgrade the target database?</p>
        <p class="mapping-preview">
          <strong>{{ mappingToUpgrade?.sourceDatabase }}</strong> →
          <strong>{{ mappingToUpgrade?.targetDatabase }}</strong>
        </p>

        <div class="warning-box" *ngIf="getVersionInfo(mappingToUpgrade?.mappingID!) as info">
          <p><strong>This will apply {{ info.versionsBehind }} version(s) with the following changes:</strong></p>
          <ul>
            <li *ngFor="let v of info.versionsToUpgrade">
              {{ v.versionName }} - {{ v.logCount }} DDL statement(s)
            </li>
          </ul>
          <p class="warning-text">⚠️ This action cannot be undone. Make sure you have a backup.</p>
        </div>

        <div class="modal-actions">
          <button class="btn btn-secondary" (click)="cancelUpgrade()" [disabled]="upgrading">Cancel</button>
          <button class="btn btn-success" (click)="executeUpgrade()" [disabled]="upgrading">
            {{ upgrading ? 'Upgrading...' : 'Upgrade Now' }}
          </button>
        </div>
      </ng-container>

      <ng-container *ngIf="upgrading && !upgradeResult">
        <div class="upgrade-progress">
          <div class="spinner"></div>
          <p>Upgrading target database...</p>
          <p class="progress-text">Please wait, this may take a few minutes.</p>
        </div>
      </ng-container>

      <ng-container *ngIf="upgradeResult">
        <div class="upgrade-result" [class.success]="upgradeResult.success" [class.error]="!upgradeResult.success">
          <div class="result-icon">{{ upgradeResult.success ? '✓' : '✗' }}</div>
          <p class="result-message">{{ upgradeResult.message }}</p>

          <div class="result-details" *ngIf="upgradeResult.success">
            <p>Versions upgraded: {{ upgradeResult.versionsUpgraded }}</p>
            <p>DDL statements applied: {{ upgradeResult.logsApplied }}</p>
          </div>

          <div class="upgrade-steps" *ngIf="upgradeResult.steps.length > 0">
            <h4>Upgrade Steps:</h4>
            <div class="step" *ngFor="let step of upgradeResult.steps"
                 [class.success]="step.success" [class.error]="!step.success">
              <span class="step-icon">{{ step.success ? '✓' : '✗' }}</span>
              <span class="step-name">{{ step.versionName }}</span>
              <span class="step-logs">{{ step.logsApplied }} log(s)</span>
              <span class="step-error" *ngIf="step.errorMessage">{{ step.errorMessage }}</span>
            </div>
          </div>
        </div>

        <div class="modal-actions">
          <button class="btn btn-primary" (click)="cancelUpgrade()">Close</button>
        </div>
      </ng-container>
    </div>
  </div>
</div>

